import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Warning, Star, User, ArrowsClockwise } from "@phosphor-icons/react";

export default function PerformanceReviews() {
    const { user } = useAuth();
    const isFounderOrHR = user?.role === "founder" || user?.role === "hr";

    const [reviews, setReviews] = useState([]);
    const [pending, setPending] = useState({ month: "", pending: [] });
    const [attention, setAttention] = useState({ alerts: [] });
    const [loading, setLoading] = useState(false);
    const [newTarget, setNewTarget] = useState(null); // employee row → open new-review dialog
    const [historyTarget, setHistoryTarget] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [r1, r2, r3] = await Promise.all([
                api.get("/performance-reviews"),
                api.get("/performance-reviews/pending-this-month"),
                isFounderOrHR ? api.get("/performance-reviews/attention")
                                : Promise.resolve({ data: { alerts: [] } }),
            ]);
            setReviews(r1.data || []);
            setPending(r2.data || { pending: [] });
            setAttention(r3.data || { alerts: [] });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [isFounderOrHR]);
    useEffect(() => { refresh(); }, [refresh]);

    // Group reviews by employee to show latest + count per employee.
    const byEmp = useMemo(() => {
        const m = {};
        for (const r of reviews) {
            (m[r.employee_id] = m[r.employee_id] || []).push(r);
        }
        return Object.values(m).map((rs) => {
            const sorted = [...rs].sort((a, b) => (b.review_date || "").localeCompare(a.review_date || ""));
            return {
                employee_id: sorted[0].employee_id,
                employee_code: sorted[0].employee_code,
                employee_name: sorted[0].employee_name,
                department: sorted[0].department,
                latest: sorted[0],
                history: sorted,
            };
        }).sort((a, b) => a.employee_name.localeCompare(b.employee_name));
    }, [reviews]);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Performance reviews"
                sub="Monthly one-on-one review per team member. Rating is visible to managers, HR and founder only."
                right={<Button variant="outline" onClick={refresh} disabled={loading} data-testid="pr-reviews-refresh">
                    <ArrowsClockwise size={14} className="mr-2" /> Refresh
                </Button>} />

            <div className="p-8 space-y-6">
                {/* Attention list — founder/HR only */}
                {isFounderOrHR && attention.alerts.length > 0 ? (
                    <div className="border rounded-md p-5"
                         style={{ borderColor: "#fecdd3", background: "#fff1f2" }}
                         data-testid="pr-attention-list">
                        <div className="flex items-center gap-2 mb-3">
                            <Warning size={18} color="#9f1239" />
                            <div className="font-medium text-rose-900">Here&apos;s what you need to know</div>
                        </div>
                        <div className="text-xs text-rose-800 mb-3">
                            Team members whose last few reviews trend low. Consider a direct conversation before this escalates.
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {attention.alerts.map((a) => (
                                <div key={a.employee_id}
                                     className="bg-white rounded-sm p-3 border"
                                     style={{ borderColor: a.tier === "red" ? "#fecdd3" : "#fde68a" }}
                                     data-testid={`pr-attention-${a.employee_code}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-medium">{a.employee_name}
                                                <span className="text-[10px] text-stone-500 ml-1">({a.employee_code}{a.department ? ` · ${a.department}` : ""})</span>
                                            </div>
                                            <div className="text-xs text-stone-600 mt-1">Avg last 3 · {a.avg_last3}/10</div>
                                        </div>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.tier === "red" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                            {a.tier.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-[11px] mt-2 text-stone-600">
                                        {a.recent_ratings.map((r) => (
                                            <span key={r.month} className="mr-2">{r.month}: {r.rating}</span>
                                        ))}
                                    </div>
                                    {a.last_note_needs_improvement ? (
                                        <div className="text-[11px] mt-2 text-stone-700 italic border-l-2 pl-2 border-rose-300">
                                            {a.last_note_needs_improvement.slice(0, 200)}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Pending this month */}
                {pending.pending.length > 0 ? (
                    <div className="border rounded-md p-5"
                         style={{ borderColor: "#fde68a", background: "#fffbeb" }}
                         data-testid="pr-pending-strip">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-medium text-amber-900">
                                Pending reviews for {pending.month} · {pending.pending.length} team member{pending.pending.length === 1 ? "" : "s"}
                            </div>
                            <div className="text-[11px] text-amber-700">Each team member needs one review per month.</div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {pending.pending.map((p) => (
                                <button key={p.id} onClick={() => setNewTarget(p)}
                                        className="text-xs bg-white border rounded-full px-3 py-1 hover:bg-amber-100 transition-colors"
                                        style={{ borderColor: "#fde68a" }}
                                        data-testid={`pr-pending-${p.employee_code}`}>
                                    <Plus size={11} className="inline mr-1" /> {p.name}
                                    <span className="text-[10px] text-stone-500 ml-1">({p.employee_code})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Employee-wise latest cards */}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        Team review history
                    </div>
                    {byEmp.length === 0 ? (
                        <div className="p-10 text-center text-stone-500 text-sm">
                            No reviews yet. Start with the pending list above.
                        </div>
                    ) : (
                        <table className="se-table" data-testid="pr-reviews-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Latest review</th>
                                    <th className="text-right">Rating</th>
                                    <th>By</th>
                                    <th>Meeting note</th>
                                    <th>Reviews on file</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {byEmp.map((e) => (
                                    <tr key={e.employee_id} data-testid={`pr-row-${e.employee_code}`}>
                                        <td>{e.employee_name}<div className="text-[10px] text-stone-500">{e.employee_code}{e.department ? ` · ${e.department}` : ""}</div></td>
                                        <td className="text-xs">{e.latest.review_date}<div className="text-[10px] text-stone-500">{e.latest.month}</div></td>
                                        <td className="se-num text-right"><RatingPill rating={e.latest.rating} /></td>
                                        <td className="text-xs">{e.latest.reviewer_name}<div className="text-[10px] text-stone-500 capitalize">{e.latest.reviewer_role}</div></td>
                                        <td className="text-xs max-w-md truncate" title={e.latest.meeting_note}>{e.latest.meeting_note}</td>
                                        <td>{e.history.length}</td>
                                        <td className="text-right">
                                            <Button size="sm" variant="outline" onClick={() => setHistoryTarget(e)}
                                                    data-testid={`pr-history-${e.employee_code}`}>History</Button>
                                            <Button size="sm" onClick={() => setNewTarget({ id: e.employee_id, name: e.employee_name, employee_code: e.employee_code })}
                                                    style={{ background: "var(--se-brand)", color: "#fff" }} className="ml-2"
                                                    data-testid={`pr-newreview-${e.employee_code}`}>
                                                <Plus size={12} className="mr-1" /> New
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <NewReviewDialog target={newTarget} onClose={() => setNewTarget(null)} onSaved={refresh} />
            <HistoryDialog target={historyTarget} onClose={() => setHistoryTarget(null)} />
        </>
    );
}

function NewReviewDialog({ target, onClose, onSaved }) {
    const [date, setDate] = useState("");
    const [rating, setRating] = useState(7);
    const [note, setNote] = useState("");
    const [right, setRight] = useState("");
    const [imp, setImp] = useState("");
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (target) {
            const d = new Date();
            setDate(d.toISOString().slice(0, 10));
            setRating(7); setNote(""); setRight(""); setImp("");
            // Fetch previous performance history for the same employee
            api.get("/performance-reviews", { params: { employee_id: target.id } })
                .then((r) => setHistory(r.data || []))
                .catch(() => setHistory([]));
        }
    }, [target]);

    if (!target) return null;

    const submit = async () => {
        if (!note.trim()) return toast.error("Meeting note is required");
        setSaving(true);
        try {
            await api.post("/performance-reviews", {
                employee_id: target.id,
                review_date: date,
                rating: Number(rating),
                meeting_note: note,
                things_done_right: right,
                needs_improvement: imp,
            });
            toast.success("Review saved");
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl" data-testid="pr-new-dialog">
                <DialogHeader>
                    <DialogTitle>Review — {target.name} <span className="text-stone-400 text-sm">({target.employee_code})</span></DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-6">
                    {/* Form — 2 cols */}
                    <div className="col-span-2 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Meeting date</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pr-new-date" /></div>
                            <div><Label>Rating (1-10)</Label>
                                <div className="flex items-center gap-2">
                                    <input type="range" min="1" max="10" value={rating}
                                           onChange={(e) => setRating(e.target.value)}
                                           className="flex-1"
                                           data-testid="pr-new-rating" />
                                    <div className="w-10"><RatingPill rating={Number(rating)} /></div>
                                </div>
                            </div>
                        </div>
                        <div><Label>Meeting note <span className="text-red-500">*</span></Label>
                            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                                      placeholder="Overall summary of what you discussed…"
                                      data-testid="pr-new-note" /></div>
                        <div><Label>Things done right</Label>
                            <Textarea rows={2} value={right} onChange={(e) => setRight(e.target.value)}
                                      placeholder="Wins, positive habits, improvements over the last review…"
                                      data-testid="pr-new-right" /></div>
                        <div><Label>Needs improvement</Label>
                            <Textarea rows={2} value={imp} onChange={(e) => setImp(e.target.value)}
                                      placeholder="Concrete gaps and what should change by next month…"
                                      data-testid="pr-new-imp" /></div>
                    </div>
                    {/* Prior history — 1 col */}
                    <div className="col-span-1 border-l pl-4" style={{ borderColor: "var(--se-border)" }}>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Previous performance</div>
                        {history.length === 0 ? (
                            <div className="text-xs text-stone-500 italic">No prior reviews for this member.</div>
                        ) : (
                            <div className="space-y-2 max-h-[420px] overflow-auto">
                                {history.map((r) => (
                                    <div key={r.id} className="bg-stone-50 rounded p-2 text-xs">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium">{r.month}</div>
                                            <RatingPill rating={r.rating} />
                                        </div>
                                        <div className="text-[10px] text-stone-500 mt-0.5">
                                            by {r.reviewer_name} · {formatDate(r.review_date)}
                                        </div>
                                        <div className="mt-1 line-clamp-2">{r.meeting_note}</div>
                                        {r.needs_improvement ? (
                                            <div className="mt-1 text-rose-700 line-clamp-2">
                                                To improve: {r.needs_improvement}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="pr-new-submit">
                        {saving ? "…" : "Save review"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function HistoryDialog({ target, onClose }) {
    if (!target) return null;
    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="pr-history-dialog">
                <DialogHeader>
                    <DialogTitle>All reviews — {target.employee_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[540px] overflow-auto">
                    {target.history.map((r) => (
                        <div key={r.id} className="border rounded-sm p-3" style={{ borderColor: "var(--se-border)" }}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium">{r.month}
                                        <span className="text-[10px] text-stone-500 ml-2">{formatDate(r.review_date)}</span>
                                    </div>
                                    <div className="text-[10px] text-stone-500">by {r.reviewer_name} · {r.reviewer_role}</div>
                                </div>
                                <RatingPill rating={r.rating} />
                            </div>
                            <div className="text-xs mt-2 whitespace-pre-wrap">{r.meeting_note}</div>
                            {r.things_done_right ? (
                                <div className="text-xs mt-2 text-emerald-700"><b>Done right:</b> {r.things_done_right}</div>
                            ) : null}
                            {r.needs_improvement ? (
                                <div className="text-xs mt-1 text-rose-700"><b>To improve:</b> {r.needs_improvement}</div>
                            ) : null}
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function RatingPill({ rating }) {
    const bg = rating >= 8 ? "#dcfce7" : rating >= 6 ? "#fef9c3" : rating >= 4 ? "#fed7aa" : "#fecdd3";
    const fg = rating >= 8 ? "#166534" : rating >= 6 ? "#854d0e" : rating >= 4 ? "#9a3412" : "#9f1239";
    return (
        <span className="text-xs font-medium px-2 py-0.5 rounded"
              style={{ background: bg, color: fg }}
              data-testid={`pr-rating-pill-${rating}`}>
            {rating}/10
        </span>
    );
}
